"use client";
import React, { useEffect, useState, useRef } from "react";
import {   LuSend } from "react-icons/lu";
import { REST_API_URL } from "../../api/config";
import { buildHeaders } from "../../api/buildHeaders";
const API_BASE = REST_API_URL;

const getUserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const getChatDisplayName = (chat) => {
  if (!chat) return "Unknown user";

  if (chat.platform === "telegram") {
    return (
      chat.full_name ||
      chat.tg_profile_name ||
      (chat.telegram_username ? `@${chat.telegram_username}` : null) ||
      (chat.tg_username ? `@${chat.tg_username}` : null) ||
      (chat.telegram_id ? `${chat.telegram_id}` : null) ||
      (chat.tg_phone ? `+${chat.tg_phone}` : null) ||
      "Unknown user"
    );
  }

  if (chat.platform === "whatsapp") {
    return chat.wa_profile_name || (chat.wa_user_id ? `+${chat.wa_user_id}` : "Unknown user");
  }

  return (
    chat.full_name ||
    (chat.instagram_username ? `@${chat.instagram_username}` : null) ||
    "Unknown user"
  );
};

const getMessagesEndpoint = (platform, chatId) => {
  const endpoints = {
    instagram: `${API_BASE}/messaging/instagram-messages/${chatId}/`,
    whatsapp: `${API_BASE}/messaging/whatsapp-messages/${chatId}/`,
    telegram: `${API_BASE}/messaging/telegram-messages/${chatId}/`,
  };

  return endpoints[platform] ?? endpoints.instagram;
};

export default function PersonalChatView({ chat, platformIcons }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const fileInputRef = useRef(null);

  const token =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");
  const platformIcon = platformIcons?.[chat.platform?.toLowerCase()];
  
  const userTimeZone = getUserTimeZone();
  
  const headers = buildHeaders();

  const [loadingMore, setLoadingMore] = useState(false);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const containerRef = useRef(null);
  const isPrependingRef = useRef(false);

  const messagesEndRef = useRef(null);

  const sanitizeUrl = (url) => {
    if (!url) return null;
    let clean = url;
    if (clean.startsWith("http://") && !clean.includes("localhost") && !clean.includes("127.0.0.1")) {
      clean = clean.replace("http://", "https://");
    }
    if (clean.startsWith("/")) {
      const base = REST_API_URL.replace(/\/api\/?$/, "");
      clean = `${base}${clean}`;
    }
    return clean;
  };

  useEffect(() => {
    if (chat?.id) loadMessages();
  }, [chat?.id, chat?.platform]);

  const scrollToBottom = (behavior = "smooth") => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior });
    }
  };

  useEffect(() => {
    if (isPrependingRef.current) return;
    if (messages.length > 0) {
      setTimeout(() => scrollToBottom("smooth"), 100);
    }
  }, [messages]);

  const loadMessages = async () => {
    if (!chat?.id) return;
    try {
      setLoading(true);
      isPrependingRef.current = false;
      const endpoint = getMessagesEndpoint(chat.platform, chat.id);

      const res = await fetch(endpoint, {
        headers,
      });

      const data = await res.json();
      const rawResults = Array.isArray(data) ? data : (data?.results ?? []);
      setMessages([...rawResults].reverse());
      const nextUrl = sanitizeUrl(data?.next);
      setNextPageUrl(nextUrl);
      setHasMore(Boolean(nextUrl));

      setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }
      }, 100);
    } catch (e) {
      console.error("❌ Error loading messages:", e);
      setMessages([]);
      setNextPageUrl(null);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  const loadOlderMessages = async () => {
    if (!nextPageUrl || loadingMore || loading) return;

    const container = containerRef.current;
    if (!container) return;

    const prevScrollHeight = container.scrollHeight;
    const prevScrollTop = container.scrollTop;

    try {
      setLoadingMore(true);

      const res = await fetch(nextPageUrl, {
        headers,
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      const rawResults = Array.isArray(data) ? data : (data?.results ?? []);
      const olderMessages = [...rawResults].reverse();

      const nextUrl = sanitizeUrl(data?.next);
      setNextPageUrl(nextUrl);
      setHasMore(Boolean(nextUrl));

      if (olderMessages.length > 0) {
        isPrependingRef.current = true;
        setMessages((prev) => {
          const existingIds = new Set(prev.map((m) => m.id));
          const filteredOlder = olderMessages.filter((m) => !existingIds.has(m.id));
          return [...filteredOlder, ...prev];
        });

        // Preserve scroll position so viewport doesn't jump
        requestAnimationFrame(() => {
          if (containerRef.current) {
            const newScrollHeight = containerRef.current.scrollHeight;
            containerRef.current.scrollTop = prevScrollTop + (newScrollHeight - prevScrollHeight);
          }
          setTimeout(() => {
            isPrependingRef.current = false;
          }, 150);
        });
      }
    } catch (e) {
      console.error("❌ Error loading older messages:", e);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleScroll = () => {
    const container = containerRef.current;
    if (!container) return;
    if (container.scrollTop <= 60 && hasMore && !loadingMore && !loading && nextPageUrl) {
      loadOlderMessages();
    }
  };

  const handleReaction = async (msg, reaction) => {
    try {
      const endpoint = `${API_BASE}/messaging/chat-message-react/${msg.id}/${chat.platform}/`;
      
      const res = await fetch(endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify({ reaction }),
      });

      const data = await res.json();
      setMessages((prev) =>
        prev.map((m) =>
          m.id === msg.id ? { ...m, reaction: data.reaction || reaction } : m
        )
      );
    } catch (e) {
      console.error("❌ Reaction failed:", e);
    }
  };

  const handleLike = (msg) => {
    const newReaction = msg.reaction === "like" ? null : "like";
    handleReaction(msg, newReaction);
  };

  const handleDislike = (msg) => {
    const newReaction = msg.reaction === "dislike" ? null : "dislike";
    handleReaction(msg, newReaction);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    const text = newMessage.trim();
    const tempId = `temp_${Date.now()}`;
    const optimisticMessage = {
      id: tempId,
      text,
      image_url: null,
      audio_url: null,
      sender: "SMB",
      timestamp: new Date().toISOString(),
      reaction: "neutral",
      isUploading: true,
    };

    setMessages((prev) => [...prev, optimisticMessage]);
    setNewMessage("");
    setErrorMessage("");

    try {
      setSending(true);
      const res = await fetch(`${API_BASE}/messaging/inapp-text-message/`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          text,
          type: "text",
          platform: chat.platform,
          fc_user_id: chat.id,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        const msgData = data?.data ?? data;
        setMessages((prev) =>
          prev.map((m) => (m.id === tempId ? { ...msgData } : m))
        );
      } else {
        const errData = await res.json().catch(() => ({}));
        const errText = errData?.error || errData?.detail || "Failed to send";
        setErrorMessage(errText);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === tempId ? { ...m, isUploading: false, isError: true, errorText: errText } : m
          )
        );
      }
    } catch (error) {
      console.error("❌ Error sending message:", error);
      const errText = "Network error, please try again";
      setErrorMessage(errText);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === tempId ? { ...m, isUploading: false, isError: true, errorText: errText } : m
        )
      );
    } finally {
      setSending(false);
    }
  };

  const compressImage = (file) =>
    new Promise((resolve) => {
      const MAX_WIDTH = 1280;
      const QUALITY = 0.8;
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        const scale = img.width > MAX_WIDTH ? MAX_WIDTH / img.width : 1;
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(resolve, "image/jpeg", QUALITY);
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(file);
      };
      img.src = objectUrl;
    });

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    const compressed = await compressImage(file);
    const tempId = `temp_${Date.now()}`;
    const localUrl = URL.createObjectURL(compressed);
    const optimisticMessage = {
      id: tempId,
      text: null,
      image_url: localUrl,
      audio_url: null,
      sender: "SMB",
      timestamp: new Date().toISOString(),
      reaction: "neutral",
      isUploading: true,
    };

    setMessages((prev) => [...prev, optimisticMessage]);

    try {
      const formData = new FormData();
      formData.append("type", "image");
      formData.append("platform", chat.platform);
      formData.append("fc_user_id", chat.id.toString());
      formData.append("file", compressed, `photo_${Date.now()}.jpg`);

      const uploadHeaders = {};
      if (token) uploadHeaders["Authorization"] = `Bearer ${token}`;
      const tz = getUserTimeZone();
      if (tz) uploadHeaders["User-Timezone"] = tz;

      const res = await fetch(`${API_BASE}/messaging/inapp-image-audio-message/`, {
        method: "POST",
        headers: uploadHeaders,
        body: formData,
      });

      if (res.ok) {
        const data = await res.json();
        const msgData = data?.data ?? data;
        URL.revokeObjectURL(localUrl);
        setMessages((prev) =>
          prev.map((m) => (m.id === tempId ? { ...msgData } : m))
        );
      } else {
        const errData = await res.json().catch(() => ({}));
        const errText = errData?.error || errData?.detail || "Failed to upload image";
        setErrorMessage(errText);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === tempId ? { ...m, isUploading: false, isError: true, errorText: errText } : m
          )
        );
      }
    } catch (error) {
      console.error("❌ Error uploading image:", error);
      const errText = "Network error, please try again";
      setErrorMessage(errText);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === tempId ? { ...m, isUploading: false, isError: true, errorText: errText } : m
        )
      );
    }
  };

  const handleStartRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = "audio/mp4";
      const recorder = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.start(100);
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      setRecordingSeconds(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingSeconds((s) => s + 1);
      }, 1000);
    } catch (err) {
      console.error("❌ Microphone access denied:", err);
    }
  };

  const handleStopRecording = async () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) return;

    clearInterval(recordingTimerRef.current);
    setIsRecording(false);
    setRecordingSeconds(0);

    recorder.stop();
    recorder.stream.getTracks().forEach((t) => t.stop());

    recorder.onstop = async () => {
      const mimeType = "audio/mp4";
      const ext = "m4a";
      const blob = new Blob(audioChunksRef.current, { type: mimeType });
      audioChunksRef.current = [];

      const localUrl = URL.createObjectURL(blob);
      const tempId = `temp_${Date.now()}`;
      const optimisticMessage = {
        id: tempId,
        text: null,
        image_url: null,
        audio_url: localUrl,
        sender: "SMB",
        timestamp: new Date().toISOString(),
        reaction: "neutral",
        isUploading: true,
      };

      setMessages((prev) => [...prev, optimisticMessage]);

      try {
        const formData = new FormData();
        formData.append("type", "audio");
        formData.append("platform", chat.platform);
        formData.append("fc_user_id", chat.id.toString());
        formData.append("file", blob, `audio_${Date.now()}.${ext}`);

        const uploadHeaders = {};
        if (token) uploadHeaders["Authorization"] = `Bearer ${token}`;
        const tz = getUserTimeZone();
        if (tz) uploadHeaders["User-Timezone"] = tz;

        const res = await fetch(`${API_BASE}/messaging/inapp-image-audio-message/`, {
          method: "POST",
          headers: uploadHeaders,
          body: formData,
        });

        if (res.ok) {
          const data = await res.json();
          const msgData = data?.data ?? data;
          URL.revokeObjectURL(localUrl);
          setMessages((prev) =>
            prev.map((m) => (m.id === tempId ? { ...msgData } : m))
          );
        } else {
          const errData = await res.json().catch(() => ({}));
          const errText = errData?.error || errData?.detail || "Failed to send audio";
          setErrorMessage(errText);
          setMessages((prev) =>
            prev.map((m) =>
              m.id === tempId ? { ...m, isUploading: false, isError: true, errorText: errText } : m
            )
          );
        }
      } catch (error) {
        console.error("❌ Error sending audio:", error);
        const errText = "Network error, please try again";
        setErrorMessage(errText);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === tempId ? { ...m, isUploading: false, isError: true, errorText: errText } : m
          )
        );
      }
    };
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    try {
      return new Date(timestamp).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: userTimeZone,
      });
    } catch {
      return "";
    }
  };

  const renderTextWithLinks = (text) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    return parts.map((part, i) =>
      urlRegex.test(part) ? (
        <a
          key={i}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="underline text-blue-300 hover:text-blue-100 break-all"
          onClick={(e) => e.stopPropagation()}
        >
          {part}
        </a>
      ) : (
        part
      )
    );
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return "";
    try {
      return new Date(timestamp).toLocaleString([], {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: "2-digit",
        minute: "2-digit",
        timeZone: userTimeZone,
      });
    } catch {
      return "";
    }
  };

  return (
    <div className="flex flex-col h-full w-full bg-white">
      <div className="flex items-center gap-3 border-b border-gray-200 p-4">
        <div className="relative">
          <img
            src={
              chat.profile_picture ||
              chat.image_url ||
              "https://cdn-icons-png.flaticon.com/512/149/149071.png"
            }
            className="w-10 h-10 rounded-full object-cover"
            alt={getChatDisplayName(chat)}
          />
          {platformIcon && (
            <img
              src={platformIcon}
              className="w-4 h-4 absolute -bottom-1 -right-1 rounded-full border-2 border-white bg-white"
              alt={chat.platform}
            />
          )}
        </div>

        <div className="flex-1">
          <h2 className="font-semibold text-gray-800 text-sm">
            {getChatDisplayName(chat)}
          </h2>
          <p className="text-xs text-gray-400 capitalize">{chat.platform}</p>
        </div>

        <button
          onClick={loadMessages}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition"
          title="Refresh messages"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </button>
      </div>

      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto p-4 space-y-4"
      >
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="text-gray-500 text-sm mt-2">Loading messages...</p>
            </div>
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
            </div>
            <p className="text-gray-500">No messages yet</p>
            <p className="text-gray-400 text-sm mt-1">Start a conversation</p>
          </div>
        ) : (
          <>
            {loadingMore && (
              <div className="flex justify-center items-center py-2 text-xs text-gray-500">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                Loading older messages...
              </div>
            )}
            {!loadingMore && hasMore && (
              <div className="flex justify-center py-1">
                <button
                  type="button"
                  onClick={loadOlderMessages}
                  className="text-xs text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-full transition font-medium"
                >
                  Load older messages
                </button>
              </div>
            )}
            {messages.map((msg) => {
            const isSelf = msg.sender === "SMB";

            return (
              <div
                key={msg.id}
                className={`flex items-start gap-3 ${
                  isSelf ? "justify-end" : ""
                }`}
              >
                <div className="max-w-[80%]">
                  {/* Bubble: only text or image — audio goes outside */}
                  {(msg.text || msg.image_url || (!msg.audio_url)) && (
                    <div
                      className={`
                        rounded-2xl overflow-hidden
                        ${msg.image_url ? "p-0" : "px-4 py-3"}
                        ${
                          isSelf
                            ? msg.isError ? "bg-red-100 text-red-700 rounded-br-none" : "bg-blue-600 text-white rounded-br-none"
                            : "bg-gray-100 text-gray-800 rounded-bl-none"
                        }
                        ${msg.isUploading ? "opacity-70" : ""}
                      `}
                    >
                      {msg.isUploading && (
                        <div className={`flex items-center gap-2 text-xs mb-1 opacity-80 ${msg.image_url ? "px-4 pt-3" : ""}`}>
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current flex-shrink-0" />
                          Sending...
                        </div>
                      )}

                      {msg.image_url && (
                        <img
                          src={msg.image_url}
                          alt="attachment"
                          className="max-w-[240px] max-h-[300px] w-full object-cover block"
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                      )}

                      {msg.text && (
                        <p className="text-sm leading-relaxed whitespace-pre-wrap break-all break-words">
                          {renderTextWithLinks(msg.text)}
                        </p>
                      )}

                      {msg.isError && (
                        <p className="text-xs mt-1 text-red-500 font-medium">
                          ⚠ {msg.errorText || "Failed to send"}
                        </p>
                      )}

                      {!msg.text && !msg.image_url && !msg.audio_url && !msg.isError && (
                        <p className="text-sm opacity-50 italic">Empty message</p>
                      )}
                    </div>
                  )}

                  {/* Audio outside bubble to avoid overflow-hidden clipping */}
                  {msg.audio_url && (
                    <div className={`mt-1 ${msg.isUploading ? "opacity-60" : ""}`}>
                      {msg.isUploading && (
                        <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-400 flex-shrink-0" />
                          Sending...
                        </div>
                      )}
                      {msg.isError && (
                        <p className="text-xs text-red-500 mb-1">
                          ⚠ {msg.errorText || "Failed to send audio"}
                        </p>
                      )}
                      <audio
                        controls
                        preload="metadata"
                        style={{ width: "280px", maxWidth: "100%", display: "block" }}
                      >
                        <source
                          src={msg.audio_url}
                          type={
                            msg.audio_url.endsWith(".m4a")
                              ? "audio/mp4"
                              : msg.audio_url.endsWith(".ogg")
                              ? "audio/ogg"
                              : "audio/webm"
                          }
                        />
                        <source src={msg.audio_url} />
                      </audio>
                    </div>
                  )}

                  <div
                    className={`flex items-center gap-2 mt-1 px-1 ${
                      isSelf ? "justify-end" : ""
                    }`}
                  >
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleLike(msg)}
                        className={`p-1 rounded-full transition ${
                          msg.reaction === "like"
                            ? "bg-blue-100 text-blue-600"
                            : "text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                        }`}
                        title="Like"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill={
                            msg.reaction === "like" ? "currentColor" : "none"
                          }
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDislike(msg)}
                        className={`p-1 rounded-full transition ${
                          msg.reaction === "dislike"
                            ? "bg-red-100 text-red-600"
                            : "text-gray-400 hover:text-red-600 hover:bg-red-50"
                        }`}
                        title="Dislike"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill={
                            msg.reaction === "dislike" ? "currentColor" : "none"
                          }
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018c.163 0 .326.02.485.06L17 4m-7 10v5a2 2 0 002 2h.095c.5 0 .905-.405.905-.905 0-.714.211-1.412.608-2.006L17 13V4m-7 10h2M17 4h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5"
                          />
                        </svg>
                      </button>
                    </div>

                    <span
                      className={`text-xs ${
                        isSelf ? "text-gray-400" : "text-gray-500"
                      }`}
                      title={formatDateTime(msg.timestamp)}
                    >
                      {formatTime(msg.timestamp)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* {errorMessage !== "" && (
        <div className="px-4 py-2 bg-red-50 border-t border-red-100">
          <p className="text-xs text-red-500">{errorMessage}</p>
        </div>
      )} */}

      <div className="border-t border-gray-200 p-4">
        <div className="flex items-center gap-2">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleImageUpload}
          />

          {/* Image upload button */}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={sending || isRecording}
            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-full transition disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
            title="Send image"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </button>

          <div className="flex-1 relative">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={isRecording ? `Recording... ${recordingSeconds}s` : "Type a message..."}
              className="w-full border border-gray-300 rounded-full px-4 py-3 pr-12 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-sm disabled:bg-gray-50"
              disabled={sending || isRecording}
            />

            <button
              onClick={handleSendMessage}
              disabled={sending || isRecording || !newMessage.trim()}
              className={`
                absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full transition
                ${!sending && !isRecording && newMessage.trim()
                  ? "bg-blue-600 text-white hover:bg-blue-700"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed"}
              `}
            >
              {sending ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <LuSend className="w-4 h-4" />
              )}
            </button>
          </div>

          {/* Mic button */}
          <button
            onClick={isRecording ? handleStopRecording : handleStartRecording}
            disabled={sending || !!newMessage.trim()}
            className={`p-2 rounded-full transition flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed
              ${isRecording
                ? "bg-red-100 text-red-600 hover:bg-red-200 animate-pulse"
                : "text-gray-500 hover:text-blue-600 hover:bg-blue-50"}
            `}
            title={isRecording ? "Stop recording" : "Record audio"}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {isRecording ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0zM9 10h6v4H9z" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              )}
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}